import { Ace } from 'ace-builds';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { getEtag, WithEtag } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { RawSchema } from '@rossum/api-client/schemas';
import { Workspace } from '@rossum/api-client/workspaces';
import { CopilotDialogV2 } from '@rossum/components/copilot-dialog/CopilotDialogV2';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { Box, CircularProgress, Stack } from '@rossum/ui/material';
import { useNavBarVisibility } from '../../components/NavBar/hooks/useNavBarVisibility';
import Editor from '../../components/UI/Editor/components/Editor';
import TopMenu from '../../components/UI/Editor/components/TopMenu';
import PoweredBy from '../../components/UI/PoweredBy';
import useDebounce from '../../utils/hooks/useDebounce';
import { useEventCallback } from '../../utils/hooks/useEventCallback';
import {
  isStreamingStatus,
  subscribeToStreamSuccess,
  useChatClientState,
} from '../chat/hooks/chatStore';
import { useCreateNewChatClient } from '../chat/hooks/useCreateChatClient';
import { notify } from '../toaster';
import { UserPanel } from '../user-panel';
import { useUser } from '../user-panel/hooks/useUser';
import { ExternalChangesNotification } from './components/ExternalChangesNotification';
import { SchemaDiffEditor } from './components/SchemaDiffEditor';
import { SchemaEditorStyles } from './components/SchemaEditorStyles';
import { SchemaEditorToolbar } from './components/SchemaEditorToolbar';
import { SchemaErrors } from './components/SchemaErrors';
import { SchemaViewSwitcher } from './components/SchemaViewSwitcher';
import {
  useFoldObjects,
  useIdAnnotations,
  usePatchRawSchema,
  usePollSchemaEtag,
  useRawSchema,
  useValidateRawSchema,
} from './hooks';
import { useAcknowledgeChanges } from './hooks/useRawSchema';
import { getErrorAnnotations } from './lib/getErrorAnnotations';
import { getIdToPathMap } from './lib/getIdToPathMap';
import { getSchemaAutoMerge } from './lib/getSchemaAutoMerge';

const schemaToString = (content: unknown): string =>
  JSON.stringify(content, null, 2) ?? '';

type SchemaEditorPageProps = {
  queue: Queue;
  workspace: Workspace | undefined;
};

export const SchemaEditorPage = ({
  queue,
  workspace,
}: SchemaEditorPageProps) => {
  const { data: schemaData, isLoading } = useRawSchema(queue.schema);

  if (isLoading || !schemaData) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '100%', width: '100%' }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <SchemaEditor queue={queue} workspace={workspace} schemaData={schemaData} />
  );
};

type SchemaEditorProps = {
  queue: Queue;
  workspace: Workspace | undefined;
  schemaData: WithEtag<RawSchema>;
};

const SchemaEditor = ({ queue, workspace, schemaData }: SchemaEditorProps) => {
  const intl = useIntl();
  const history = useHistory<{ backLink?: string }>();
  const { acknowledgeChanges } = useAcknowledgeChanges(queue.schema);

  const { data: user } = useUser();

  const {
    create,
    isLoading: isCreating,
    error: createError,
  } = useCreateNewChatClient();

  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);

  const { status, error: streamError } = useChatClientState(clientId);
  const isStreaming = isStreamingStatus(status);

  const fabryErrorMessage =
    createError instanceof Error ? createError.message : streamError?.message;

  const onClick = ({ input }: { input: string }) => {
    const selectedText = aceEditor?.getSelectedText();
    const selectionContext = selectedText
      ? `\n\nSelected portion of the schema (focus here if relevant):\n\`\`\`json\n${selectedText}\n\`\`\``
      : '';
    const initialMessage = `A user wants you to generate a Rossum schema for a queue. If you have enough information, return only the updated schema as a JSON code block (\`\`\`json ... \`\`\`) and nothing else. If you need clarification, ask a follow-up question instead.\n\nCurrent schema:\n\`\`\`json\n${schemaValue}\n\`\`\`${selectionContext}\n\nUser query: ${input}`;
    create(client => {
      client.sendAgentMessage(initialMessage, 'read-only', {});
      setClientId(client.chatId);
    });
  };

  const originalSchemaString = useMemo(
    () => schemaToString(schemaData.content),
    [schemaData.content]
  );

  const [schemaValue, setSchemaValue] = useState<string>(originalSchemaString);

  // Debounced value for expensive operations (annotation widgets, error positions).
  // The editor itself always receives the immediate schemaValue so react-ace never
  // sees a stale value prop and reverts the editor content mid-typing.
  const debouncedSchemaValue = useDebounce(schemaValue, 300);
  const [aceEditor, setAceEditor] = useState<Ace.Editor | null>(null);

  const patchMutation = usePatchRawSchema(queue.schema);

  const { isJSONValid, parsedContent, isPrettified } = useMemo(() => {
    try {
      const parsed = JSON.parse(schemaValue);
      const prettified = schemaValue === schemaToString(parsed);
      return {
        isJSONValid: true,
        parsedContent: parsed,
        isPrettified: prettified,
      };
    } catch {
      return { isJSONValid: false, parsedContent: null, isPrettified: false };
    }
  }, [schemaValue]);
  const [isDiffModeEnabled, setIsDiffModeEnabled] = useState(false);

  const { isVisible: isUserPanelVisible } = useNavBarVisibility();

  const { remoteSchema, checkForChanges, isCheckingForChanges } =
    usePollSchemaEtag(queue.schema, {
      initialEtag: getEtag(schemaData),
    });

  const polledEtag = remoteSchema ? getEtag(remoteSchema) : undefined;
  const initialEtag = getEtag(schemaData);

  useEffect(
    () =>
      clientId
        ? subscribeToStreamSuccess(clientId, state => {
            const lastText =
              state.sessions[clientId]?.messages
                .findLast(m => m.role === 'assistant')
                ?.parts.findLast(p => p.type === 'text')?.text ?? '';
            const json = lastText.match(/^```json\n([\s\S]*)\n```$/)?.[1];
            if (json !== undefined) {
              setSchemaValue(json);
              setOpen(false);
              setIsDiffModeEnabled(true);
            } else {
              const client = state.clients[clientId];
              if (client) {
                state.setDrawerClient(client);
                state.setIsOpen(true);
              }
              setOpen(false);
            }
          })
        : undefined,
    [clientId]
  );

  const hasExternalChanges =
    polledEtag !== undefined &&
    initialEtag !== undefined &&
    polledEtag !== initialEtag;

  const handleRefresh = useCallback(() => {
    if (remoteSchema) {
      acknowledgeChanges(remoteSchema);
      setIsDiffModeEnabled(false);
      setSchemaValue(schemaToString(remoteSchema.content));
    }
  }, [acknowledgeChanges, remoteSchema]);

  const handleKeepEditing = useCallback(() => {
    if (remoteSchema) {
      acknowledgeChanges(remoteSchema);
      setIsDiffModeEnabled(false);
    }
  }, [acknowledgeChanges, remoteSchema]);

  const isSchemaChanged = schemaValue !== originalSchemaString;

  const { canAutoMerge, mergedContent } = useMemo(
    () =>
      getSchemaAutoMerge({
        originalContent: schemaData.content,
        localContent: parsedContent,
        remoteContent: remoteSchema?.content,
        hasExternalChanges,
        hasLocalChanges: isSchemaChanged,
      }),
    [
      schemaData.content,
      parsedContent,
      remoteSchema?.content,
      hasExternalChanges,
      isSchemaChanged,
    ]
  );

  const handleViewChange = useCallback((view: 'editor' | 'diff') => {
    setIsDiffModeEnabled(view === 'diff');
  }, []);

  const handleMerge = useCallback(() => {
    if (canAutoMerge && mergedContent && remoteSchema) {
      acknowledgeChanges(remoteSchema);
      setSchemaValue(schemaToString(mergedContent));
    }
  }, [canAutoMerge, mergedContent, remoteSchema, acknowledgeChanges]);

  const handleRemoteDataReceived = useEventCallback(() => {
    if (!hasExternalChanges || !remoteSchema) {
      return;
    }

    const isModifiedByCurrentUser =
      user?.url !== undefined && remoteSchema?.modifiedBy === user.url;

    if (canAutoMerge && isModifiedByCurrentUser) {
      acknowledgeChanges(remoteSchema);
      setSchemaValue(schemaToString(mergedContent));
      notify.default({
        title: intl.formatMessage({
          id: 'features.schemaEditor.toast.merged',
        }),
      });
    } else {
      setIsDiffModeEnabled(true);
    }
  });

  useEffect(() => {
    if (remoteSchema?.__etag__) {
      handleRemoteDataReceived();
    }
  }, [handleRemoteDataReceived, remoteSchema?.__etag__]);

  const idToPathMap = useMemo(
    () => getIdToPathMap(schemaData.content),
    [schemaData.content]
  );

  const handleIdClick = useCallback(
    (idValue: string) => {
      const fieldPath = idToPathMap.get(idValue);
      if (fieldPath) {
        window.open(
          `/queues/${queue.id}/settings/fields/${fieldPath}`,
          '_blank'
        );
      }
    },
    [idToPathMap, queue.id]
  );

  const schemaFields = useIdAnnotations(aceEditor, debouncedSchemaValue, {
    onIdClick: handleIdClick,
  });

  const { toggleFold, isFolded } = useFoldObjects(aceEditor, schemaValue);

  const handleToggleFold = useCallback(() => {
    if (!isPrettified && isJSONValid && parsedContent !== null) {
      setSchemaValue(schemaToString(parsedContent));
    }
    toggleFold();
  }, [isPrettified, isJSONValid, parsedContent, toggleFold]);

  const idToScrollTo = useMemo(
    () => new URLSearchParams(history.location.search).get('id'),
    [history.location.search]
  );

  const scrollToId = useEventCallback((id: string) => {
    const field = schemaFields.find(f => f.id === id);
    if (field && aceEditor) {
      aceEditor.gotoLine(field.row + 1, 0, true);
      aceEditor.focus();
    }
  });

  useEffect(() => {
    if (aceEditor && idToScrollTo) {
      scrollToId(idToScrollTo);
    }
  }, [aceEditor, idToScrollTo, scrollToId]);

  const handleFieldSelect = useCallback(
    (field: { id: string }) => {
      history.replace({
        search: `?id=${field.id}`,
        state: history.location.state,
      });
    },
    [history]
  );

  const { data: schemaErrors = [] } = useValidateRawSchema(
    parsedContent,
    isJSONValid && parsedContent !== null
  );

  const handleChange = useCallback((value: string) => {
    setSchemaValue(value);
  }, []);

  const isValid = isJSONValid && schemaErrors.length === 0;
  const canSave = isSchemaChanged && isValid && !patchMutation.isLoading;
  const canReset = isSchemaChanged || !isJSONValid;

  const handleSave = useCallback(() => {
    if (!canSave || parsedContent === null) return;
    patchMutation.mutate(
      { content: parsedContent, etag: getEtag(schemaData) },
      {
        onSuccess: data => {
          setSchemaValue(schemaToString(data.content));
          setIsDiffModeEnabled(false);
        },
      }
    );
  }, [canSave, parsedContent, patchMutation, schemaData]);

  const handleReset = useCallback(() => {
    if (!canReset) return;

    setSchemaValue(originalSchemaString);
    notify.default({
      title: intl.formatMessage({ id: 'features.schemaEditor.toast.reverted' }),
    });
  }, [canReset, originalSchemaString, intl]);

  const handlePrettify = useCallback(() => {
    if (!isJSONValid || parsedContent === null) return;
    setSchemaValue(schemaToString(parsedContent));
  }, [isJSONValid, parsedContent]);

  const canPrettify = isJSONValid && !isPrettified;

  const handleNavigateBack = useCallback(() => {
    const backLink =
      history.location.state?.backLink ?? `/queues/${queue.id}/settings/fields`;
    history.push(backLink);
  }, [history, queue.id]);

  const handleAddSectionClick = useCallback(() => {
    const newUuid = uuidv4();
    window.open(`/queues/${queue.id}/settings/fields/${newUuid}`, '_blank');
  }, [queue.id]);

  const errorAnnotations = useMemo(
    () => getErrorAnnotations(debouncedSchemaValue, schemaErrors),
    [debouncedSchemaValue, schemaErrors]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
      data-page-title="queue-schema"
    >
      <TopMenu
        onNavigateBack={handleNavigateBack}
        canNavigateBack
        onPrettify={handlePrettify}
        canPrettify={canPrettify}
        onSave={handleSave}
        canSave={canSave}
        onReset={handleReset}
        canReset={canReset}
        isValid={isValid}
        isChanged={isSchemaChanged}
        isSaving={patchMutation.isLoading}
        title={workspace ? `${workspace.name} / ${queue.name}` : queue.name}
        right={
          <>
            <SchemaViewSwitcher
              activeView={isDiffModeEnabled ? 'diff' : 'editor'}
              onViewChange={handleViewChange}
            />
            <PoweredBy />
            {isUserPanelVisible ? <UserPanel /> : null}
          </>
        }
        editorType="schema"
      />

      <CopilotDialogV2
        title={intl.formatMessage({ id: 'features.schemaEditor.fabry.title' })}
        suggestingTitle={intl.formatMessage({
          id: 'features.schemaEditor.fabry.suggestingThinking',
        })}
        open={open}
        isError={fabryErrorMessage !== undefined}
        errorText={
          fabryErrorMessage ||
          intl.formatMessage({ id: 'features.schemaEditor.fabry.error' })
        }
        subtitle={intl.formatMessage({
          id: 'features.schemaEditor.fabry.subtitle',
        })}
        placeholder={intl.formatMessage({
          id: 'features.schemaEditor.fabry.placeholder',
        })}
        onSubmit={value => onClick({ input: value })}
        onClose={() => setOpen(false)}
        isSuggesting={isCreating || isStreaming}
        confirmText={intl.formatMessage({
          id: 'features.schemaEditor.fabry.confirm',
        })}
      />

      {!isDiffModeEnabled && (
        <SchemaEditorToolbar
          onToggleFold={handleToggleFold}
          isFolded={isFolded}
          schemaFields={schemaFields}
          onFieldSelect={handleFieldSelect}
          isJSONValid={isJSONValid}
          onAddSectionClick={handleAddSectionClick}
          onCheckForChanges={checkForChanges}
          isCheckingForChanges={isCheckingForChanges}
          onGenerateSchema={() => setOpen(true)}
        />
      )}

      <Box sx={{ flex: 1, position: 'relative' }}>
        <SchemaEditorStyles />
        {isDiffModeEnabled ? (
          <SchemaDiffEditor
            localValue={schemaValue}
            remoteValue={
              remoteSchema
                ? schemaToString(remoteSchema.content)
                : originalSchemaString
            }
          />
        ) : (
          <Editor
            name="schemaEditor"
            mode="json"
            onChange={handleChange}
            value={schemaValue}
            annotations={errorAnnotations}
            onLoad={setAceEditor}
          />
        )}
        {(schemaErrors.length > 0 || hasExternalChanges) && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 10,
            }}
          >
            {hasExternalChanges && (
              <ExternalChangesNotification
                onDismiss={handleKeepEditing}
                onMerge={handleMerge}
                onRefresh={handleRefresh}
                canMerge={canAutoMerge}
              />
            )}

            {schemaErrors.length > 0 && <SchemaErrors errors={schemaErrors} />}
          </Box>
        )}
      </Box>
      <LeavingDialog when={isSchemaChanged || !isJSONValid} />
    </Box>
  );
};
